import type { CropRect, Preset, Scenario } from "./types";

export const TALK_FROM_LECTERN_MASTER_URL = "/scenes/2k/talk-from-lectern.master.jpg";
export const TALK_FROM_LECTERN_MASTER_W = 2048;
export const TALK_FROM_LECTERN_MASTER_H = 1143;

// Stage geometry (per multiview-visual-contract.md): speaker stands behind a
// centered lectern with a lectern-mounted microphone; the chairman's mic on a
// chrome floor stand is alone on audience-left; audience-right is empty stage.
// Crops below are normalized (0..1) over the 2048x1143 2K master generated
// from the original 5504x3072 Gemini 3 Pro Image master (2026-05-08).
export const TALK_FROM_LECTERN_PRESETS = {
  // CO-160 §4 Speaker MCU — head + shoulders + top of lectern.
  speakerMcu: { x: 0.42, y: 0.28, width: 0.18, height: 0.46 },
  // Tighter framing on the speaker's head for a "talking head" look.
  speakerCu: { x: 0.45, y: 0.30, width: 0.12, height: 0.34 },
  // Wider context: lectern + speaker with breathing room.
  lecternWide: { x: 0.36, y: 0.22, width: 0.30, height: 0.58 },
  // Slight off-center tilt favoring stage-right side of the speaker (CAM 1 flavor).
  speakerWideRight: { x: 0.40, y: 0.20, width: 0.36, height: 0.62 },
  // CO-160 §4 Stage Wide — full stage edge-to-edge.
  stageWide: { x: 0.04, y: 0.10, width: 0.92, height: 0.78 },
  // Stage-left context including the chairman's mic stand.
  chairmanMicWide: { x: 0.20, y: 0.18, width: 0.50, height: 0.66 },
} as const satisfies Record<string, CropRect>;

// Per-cam preset library for the talk-from-lectern scenario. Display order
// is the array order; AW-RP50 numeric buttons (1..10) bind to the first 10.
// CAM 1 (audience-left of array) → favors stage-right framings of the speaker.
// CAM 2 (centered) → centered lectern (speaker MCU / CU).
// CAM 3 (audience-right of array) → audience-left / wide / chairman's mic side.
const cam1Presets: Preset[] = [
  { id: "speaker-wide-right", label: "Speaker Wide R", cam: "cam1", scenarioId: "talk-from-lectern", crop: TALK_FROM_LECTERN_PRESETS.speakerWideRight },
  { id: "speaker-mcu-r", label: "Speaker MCU R", cam: "cam1", scenarioId: "talk-from-lectern", crop: TALK_FROM_LECTERN_PRESETS.speakerMcu },
];

const cam2Presets: Preset[] = [
  { id: "speaker-mcu", label: "Speaker MCU", cam: "cam2", scenarioId: "talk-from-lectern", crop: TALK_FROM_LECTERN_PRESETS.speakerMcu },
  { id: "speaker-cu", label: "Speaker CU", cam: "cam2", scenarioId: "talk-from-lectern", crop: TALK_FROM_LECTERN_PRESETS.speakerCu },
  { id: "lectern-wide", label: "Lectern Wide", cam: "cam2", scenarioId: "talk-from-lectern", crop: TALK_FROM_LECTERN_PRESETS.lecternWide },
];

const cam3Presets: Preset[] = [
  { id: "stage-wide", label: "Stage Wide", cam: "cam3", scenarioId: "talk-from-lectern", crop: TALK_FROM_LECTERN_PRESETS.stageWide },
  { id: "chairman-mic-wide", label: "Chairman Wide", cam: "cam3", scenarioId: "talk-from-lectern", crop: TALK_FROM_LECTERN_PRESETS.chairmanMicWide },
];

export const TALK_FROM_LECTERN: Scenario = {
  id: "talk-from-lectern",
  label: "Talk from Lectern",
  master: {
    url: TALK_FROM_LECTERN_MASTER_URL,
    width: TALK_FROM_LECTERN_MASTER_W,
    height: TALK_FROM_LECTERN_MASTER_H,
  },
  presets: { cam1: cam1Presets, cam2: cam2Presets, cam3: cam3Presets },
  defaults: {
    cam1: "speaker-wide-right",
    cam2: "speaker-mcu",
    cam3: "stage-wide",
  },
};
